export interface WebRequest<T> {
  data: T | null;
  status: WebRequestStatus;
}

export enum WebRequestStatus {
  READY,
  SUCCESS,
  FAILURE,
  IN_PROGRESS
}

// From least -> furthest progressed
//
// I guess, counterintuitively, ready is synonomous with "complete"
const progressionOrder = {
  [WebRequestStatus.IN_PROGRESS]: 1,
  [WebRequestStatus.SUCCESS]: 2,
  [WebRequestStatus.FAILURE]: 3,
  [WebRequestStatus.READY]: 4
};

export const webRequestUtils = {
  leastProgressed(requests: WebRequestStatus[]) {
    return requests.reduce((prev, current) => {
      return progressionOrder[prev] < progressionOrder[current]
        ? prev
        : current;
    }, WebRequestStatus.READY);
  }
};

export interface BaseOrganization {
  organizationId: number;
  name: string;
  createdAt: Date;
  exhibitionCount?: number | undefined;
  roleNameStudent: string;
  roleNameStudentPlural: string;
  roleNameTeacher: string;
  roleNameTeacherPlural: string;
  ltiPending?: boolean;
}

export interface BaseUser {
  userId: number;
  email: string;
  name?: string;
  pictureUrl?: string;
  emails: UserEmail[];
}

export type RelationshipManager = {
  type: string;
};

export interface UserEmail {
  userEmailId: number;
  email: string;
}

export interface BasePeerGroup {
  peerGroupId: number;
  createdById: number;
  name: string;
  section: string;
  description: string;
  isActive: boolean;
  isGoogleClassroom: boolean;
  isLtiManaged: boolean;
  creatorCanPublish: boolean;
  teachersCanPublish: boolean;
  submissionStatusIsInteractive: boolean;
  organizationSyncAssignments: boolean;
  roleNameStudent: string;
  roleNameStudentPlural: string;
  roleNameTeacher: string;
  roleNameTeacherPlural: string;
  createdAt: Date;
  updatedAt: Date;
  alias?: string;
}

interface ApiPacketConnection {
  packetId: number;
}

export interface ApiPeerGroupMembership {
  user: BaseUser;
  role: MemberRole;
  manager: RelationshipManager;
  managerReference?: string;
}

export interface ApiPeerGroup extends BasePeerGroup {
  memberships: ApiPeerGroupMembership[];
  packets: ApiPacketConnection[];
}

export interface ApiOrgMember {
  member: BaseUser;
  isAdmin: boolean;
  isOrganizer: boolean;
  isHomeOrg: boolean;
}

// A user and their roles in the context of an org
// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface OrgMembership extends ApiOrgMember {
  isOrphan: boolean;
}

export enum MemberRole {
  STUDENT = 'STUDENT',
  TEACHER = 'TEACHER',
  OBSERVER = 'OBSERVER'
}

export enum OrgRole {
  ADMIN = 'ADMIN',
  ORGANIZER = 'ORGANIZER',
  MEMBER = 'MEMBER'
}

export interface GettingStartedPeerGroup extends ApiPeerGroup {
  organization: BaseOrganization;
}

export const convertPeerGroup = ({
  peerGroupId,
  createdById,
  name,
  section,
  description,
  alias,
  isActive,
  creatorCanPublish,
  teachersCanPublish,
  submissionStatusIsInteractive,
  organizationSyncAssignments,
  roleNameStudent,
  roleNameStudentPlural,
  roleNameTeacher,
  roleNameTeacherPlural,
  createdAt,
  updatedAt
}: BasePeerGroup): BasePeerGroup => ({
  peerGroupId,
  createdById,
  name,
  section,
  description,
  isActive,
  creatorCanPublish,
  teachersCanPublish,
  submissionStatusIsInteractive,
  organizationSyncAssignments,
  isGoogleClassroom: !!alias && alias.startsWith('g'),
  isLtiManaged: !!alias && alias.startsWith('o'),
  roleNameStudent,
  roleNameStudentPlural,
  roleNameTeacher,
  roleNameTeacherPlural,
  createdAt,
  updatedAt,
  alias
});
